import { useMemo } from 'react';
import { Box, Chip } from '@mui/material';
import { ColDef, RowStyle } from 'ag-grid-community';
import type { CellClassParams, ColSpanParams, RowSpanParams } from 'ag-grid-community';
import type { RowClassParams } from 'ag-grid-community/dist/types/core/entities/gridOptions';
import { BudgetScenarioImpactResult, round2 } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import {
    formatCurrency,
    generateMonthColumns,
    SCENARIO_GRID_CLASS,
    SCENARIO_HEADER_HEIGHT,
    SCENARIO_ROW_HEIGHT,
    SCENARIO_MONTH_COL_WIDTH,
    SCENARIO_ROW_LABEL_COL_WIDTH,
    scenarioEntityColumn,
} from './helpers';

interface ScenarioImpactGridProps {
    impact: BudgetScenarioImpactResult | undefined;
    isLoading: boolean;
    // Header for the pinned-left entity column — a worktype (Contacts) or a partner/location
    // (Minute-price). Defaults to the worktype label. Ignored where the rows carry `entityLabelParts`
    // (Shift), whose columns are captioned from the part keys instead; it stays the fallback for a
    // result that predates them.
    entityHeader?: string;
    // The form has unsaved edits AND they could not be previewed (an invalid row, or the preview request
    // failed), so these numbers were computed from the LAST SAVED inputs — the table is dimmed to say it is
    // not describing what is on screen. The words for it are ScenarioImpactBadge's, in the section heading.
    isStale?: boolean;
}

const TOTAL_FIELD = 'rowTotal';
const ENTITY_FIELD = 'entityLabel';

/** The class App.css styles a training subrow by (muted, so the pair reads as one entry). */
const TRAINING_ROW_CLASS = 'scenario-training-subrow';

/** …and the one on its LABEL cell, which App.css right-aligns so "Trainingskosten" ends where the
 *  figures it describes begin, instead of floating at the far left of a 160px identity column. */
const TRAINING_LABEL_CLASS = 'scenario-training-label';

/** ag-grid applies this per cell: the label class lands on the training subrow's label cell only, never
 *  on the main row's identity beside it. */
const trainingLabelRule = {
    [TRAINING_LABEL_CLASS]: (params: CellClassParams): boolean => Boolean(params.data?.isTraining),
};

/** ag-grid field (and colId) of one part of a split row identity. Prefixed rather than used raw: the row
 *  object is FLAT — month keys, `rowTotal` and `trainingTotal` are its siblings — and the part keys come
 *  over the wire, so an unprefixed 'rowTotal' part would overwrite the total column's own value. */
const partField = (key: string): string => `part_${key}`;

/** Which locale key captions each identity part the backend can send (CALL-783).
 *
 *  The DTO carries machine keys only, so the headers stay here — and they are the SAME three keys the shift
 *  INPUT grid uses for its own Working Unit / Von / Nach columns, so the two halves of the toggle cannot
 *  drift apart or be translated differently. A key this map does not know prints raw: an oddly captioned
 *  column is a visible bug report, a dropped one silently loses a row's identity. */
const ENTITY_PART_HEADER_KEYS: Record<string, string> = {
    workingUnit: 'field.workingUnit',
    from: 'field.shiftFrom',
    to: 'field.shiftTo',
};

/** The chip that qualifies impact figures which are not the saved state — a preview of the current draft, or
 *  the last saved numbers where that draft could not be previewed.
 *
 *  Rendered by the SECTION HEADING (see ScenarioSection's `badge`), not above the table it describes, which
 *  is where it used to live in both impact grids. Only the impact view has such a chip, so appearing there
 *  moved the grid — and everything under it — down by 32px on every toggle of a dirty draft; measured on all
 *  five types (CALL-784). The heading is a row both views render, and its minHeight already fits a chip.
 *
 *  Shared by both impact grids rather than duplicated: they said the same two things in the same two colours. */
export const ScenarioImpactBadge = ({ isPreview, isStale }: { isPreview?: boolean; isStale?: boolean }) => {
    const { t } = useTranslation('budget');
    if (!isPreview && !isStale) return null;
    return (
        <Chip
            size="small"
            color={isPreview ? 'info' : 'warning'}
            label={<span>{t(isPreview ? 'scenarioDetail.impactPreview' : 'scenarioDetail.impactStale')}</span>}
        />
    );
};

// Read-only single-scenario impact: cost impact per entity × month (calculated, non-editable), a
// per-entity total column, and a pinned totals row (per-month + grand total). Calculated cells are
// visually distinct from the yellow editable input grid. The entity is a worktype (Contacts) or a
// partner/location (Minute-price); the calc shell emits generic entityId/entityLabel rows.
//
// The identity columns are data-driven rather than fixed, for Verschiebung (CALL-783), whose identity is
// not one thing: where the rows carry `entityLabelParts` the identity is rendered as one column PER PART
// (Working Unit · Von · Nach) instead of as the composite label it used to cram into the single pinned
// column. Absent for every other type, which therefore renders exactly as before.
//
// A row whose scenario books training cost becomes TWO grid rows (CALL-801): the main row shows the
// operational impact WITHOUT training, and a muted subrow beneath it carries the monthly training slices —
// labelled "Trainingskosten" and spanning the identity: the first identity cell rowSpans over both. This
// replaced the row-level Trainingskosten column, which duplicated the same euros inside the month cells and
// said nothing about WHEN they are charged. Both rows' Gesamt are the sum of their own visible month cells,
// so the column still adds up to the pinned totals row (the server's monthlyTotals/grandTotal include
// training), and the bottom line is unchanged.
//
// The title lives in the section header, where it doubles as the input⇄impact toggle (CALL-783), and so does
// the badge that qualifies the numbers when they are not the saved state (CALL-784).
const ScenarioImpactGrid = ({ impact, isLoading, entityHeader, isStale }: ScenarioImpactGridProps) => {
    const { t } = useTranslation('budget');

    const months = impact?.months ?? [];

    // A split identity (Shift: working unit, Von, Nach) or the one composite label every other type emits.
    // Read off the FIRST row: a calc that splits its identity splits it for every row it returns, so this is
    // the shape of the whole result — and an empty array is the pre-CALL-783 single-column behaviour.
    const labelParts = useMemo(() => impact?.rows[0]?.entityLabelParts ?? [], [impact]);
    // Whichever identity column comes first carries the pinned totals row's "Gesamt": with a split identity
    // the composite `entityLabel` column is not rendered at all, so writing the word there would print it
    // nowhere.
    const firstIdentityField = labelParts.length > 0 ? partField(labelParts[0].key) : ENTITY_FIELD;
    // Where the training row's "Trainingskosten" label lands: beside the spanning identity cell where the
    // identity is split (the Von column, colSpanned over Nach), in the identity column itself otherwise —
    // a single-identity type has no second column to put it in, so there the pair reads as an indented line.
    const trainingLabelField = labelParts.length > 1 ? partField(labelParts[1].key) : firstIdentityField;

    const rowData = useMemo(() => {
        if (!impact) return [];
        return impact.rows.flatMap((row) => {
            const cells = impact.months.map((month) => {
                const cell = row.monthly[month];
                return { month, impact: cell?.impact ?? 0, trainingCost: cell?.trainingCost ?? 0 };
            });
            // A subrow only where the row actually books training euros: shift emits trainingTotal: 0 for
            // an untrained row, and an all-zero subrow under every row would drown the ones that matter.
            const hasTraining =
                row.trainingTotal != null && (row.trainingTotal !== 0 || cells.some((cell) => cell.trainingCost !== 0));

            const main: Record<string, unknown> = {
                entityId: row.entityId,
                [ENTITY_FIELD]: row.entityLabel,
                // The identity cell stretches over both rows of the pair (rowSpan on the first column).
                trainingSpan: hasTraining && labelParts.length > 1,
            };
            // Beside the composite label, not instead of it: the parts are what the columns read, while
            // entityLabel stays the row's one authoritative identity (and getRowId keys on entityId).
            for (const part of row.entityLabelParts ?? []) main[partField(part.key)] = part.value;

            if (!hasTraining) {
                main[TOTAL_FIELD] = row.rowTotal;
                for (const cell of cells) main[cell.month] = cell.impact;
                return [main];
            }

            // The pair (CALL-801): the main row's cells EXCLUDE training (the pure operational effect —
            // for shift that is −savings), the subrow carries the monthly training slices. Re-rounded with
            // the SHARED round2, so the main total is exactly the sum of its visible cells.
            let mainTotal = 0;
            for (const cell of cells) {
                const value = round2(cell.impact - cell.trainingCost);
                main[cell.month] = value;
                mainTotal += value;
            }
            main[TOTAL_FIELD] = round2(mainTotal);

            let trainingTotal = 0;
            for (const cell of cells) trainingTotal += cell.trainingCost;

            const training: Record<string, unknown> = {
                entityId: row.entityId,
                isTraining: true,
                [trainingLabelField]: t('field.trainingTotal'),
                // The training ALLOCATED to these months, i.e. the sum of the cells beside it — not the
                // row's unclipped `trainingTotal`, which is a different quantity (an entered window
                // reaching outside the scenario period keeps its euros, and the months lose them; see
                // BudgetScenarioImpactRow.trainingTotal). Printing the unclipped figure here left the
                // Gesamt column not adding up to the pinned grand total under it, which reads as a broken
                // table; the input grid's own Trainingskosten column is where the full cost is shown.
                [TOTAL_FIELD]: round2(trainingTotal),
            };
            for (const cell of cells) training[cell.month] = cell.trainingCost;
            return [main, training];
        });
    }, [impact, labelParts, trainingLabelField, t]);

    const pinnedBottomRowData = useMemo(() => {
        if (!impact || impact.rows.length === 0) return [];
        const totals: Record<string, unknown> = {
            [firstIdentityField]: t('summary.total'),
            [TOTAL_FIELD]: impact.grandTotal,
        };
        // The server's totals include training, so the bottom row is visibly the sum of every main row
        // AND every training subrow above it — the one place the whole picture adds back up.
        for (const month of impact.months) totals[month] = impact.monthlyTotals[month] ?? 0;
        return [totals];
    }, [impact, t, firstIdentityField]);

    const columnDefs: ColDef[] = useMemo(
        () => [
            // ── The identity ────────────────────────────────────────────────────────────────────────────
            // One column per part where the row carries them (Shift), otherwise the single composite column
            // every other type has always had — Kontakte, Minutenpreis and GA/CB/VVL are untouched by this.
            //
            // Only the FIRST part pins, exactly as in the input grid this toggles with (ScenarioShiftGrid):
            // it is what a row is read by while everything else scrolls. Von and Nach scroll with the months
            // at SCENARIO_ROW_LABEL_COL_WIDTH — the input grid's own width, which is what keeps the table
            // from moving sideways on every toggle.
            //
            // What it costs, measured in the browser at a 1400px viewport (the 240px nav drawer expanded, so
            // the grid shell is 1120px): the pinned 160 and the pinned 130px Gesamt sit outside a 830px centre
            // viewport, and the three scrolling leading columns (115 + 115 + 110 Trainingskosten) take 340 of
            // it — leaving 490px, FOUR whole month columns at 110 plus 50px of a fifth. The single composite
            // column left all 830px to the months, i.e. seven of them; naming Von and Nach and showing the
            // training cost is what the client asked for instead, and the rest scroll behind the pinned
            // Gesamt, which App.css gives a shadow so it reads as an edge.
            ...(labelParts.length > 0
                ? labelParts.map((part, index) => ({
                      ...scenarioEntityColumn(
                          ENTITY_PART_HEADER_KEYS[part.key] ? t(ENTITY_PART_HEADER_KEYS[part.key]) : part.key
                      ),
                      field: partField(part.key),
                      ...(index === 0
                          ? {
                                // The identity cell of a row with a training subrow stretches over both rows
                                // of the pair (CALL-801). Legacy span API: needs suppressRowTransform on the
                                // grid, and the spanning cell needs its own opaque background (App.css) so the
                                // subrow's empty cell does not show through.
                                rowSpan: (params: RowSpanParams) => (params.data?.trainingSpan ? 2 : 1),
                                cellClassRules: {
                                    'scenario-entity-span': (params: CellClassParams): boolean =>
                                        Boolean(params.data?.trainingSpan),
                                    // Only where this column IS the label column — a single-part identity,
                                    // which no type emits today but which the label field already handles.
                                    ...(partField(part.key) === trainingLabelField ? trainingLabelRule : {}),
                                },
                            }
                          : {
                                pinned: undefined,
                                width: SCENARIO_ROW_LABEL_COL_WIDTH,
                                minWidth: SCENARIO_ROW_LABEL_COL_WIDTH,
                                // The training subrow's "Trainingskosten" label sits in the Von column and
                                // spans over Nach — the move's endpoints belong to the main row, the label
                                // to the euros beside it.
                                ...(index === 1
                                    ? {
                                          colSpan: (params: ColSpanParams) => (params.data?.isTraining ? 2 : 1),
                                          cellClassRules: trainingLabelRule,
                                      }
                                    : {}),
                            }),
                  }))
                : [
                      {
                          ...scenarioEntityColumn(entityHeader ?? t('field.worktype')),
                          field: ENTITY_FIELD,
                          // The types with one identity column carry the training label in it, so that is
                          // the cell to right-align.
                          cellClassRules: trainingLabelRule,
                      },
                  ]),
            // A `width`, not the helper's minWidth: month columns are flex:0, where ag-grid sizes by width
            // and defaults it to 200px — see SCENARIO_MONTH_COL_WIDTH. 110 is what a signed six-figure euro
            // cell needs, which is why the impact view keeps it where the row INPUT grids went narrower.
            ...generateMonthColumns(months, { width: SCENARIO_MONTH_COL_WIDTH }),
            // No trailing filler here, unlike the input grids: the pinned Gesamt below already sits flush
            // against the shell's right border, so a flex column in front of it does not fill the table — it
            // stretches an empty bordered box between the last month and the total. On a three-month scenario
            // that measured 427px, which read as a broken table (CALL-784). The leftover stays background.
            {
                headerName: t('summary.total'),
                headerTooltip: t('summary.total'),
                field: TOTAL_FIELD,
                width: 130,
                minWidth: 130,
                pinned: 'right',
                flex: 0,
                cellStyle: { textAlign: 'right', fontWeight: 'bold' },
                valueFormatter: (params) => formatCurrency(params.value),
            },
        ],
        [t, months, entityHeader, labelParts, trainingLabelField]
    );

    // Whether any row/training pair is actually on screen — sorting has to go only where it could tear
    // one apart. A Minutenpreis or Budget table (and any Kontakte/GA-CB-VVL scenario without training
    // euros) renders single rows and keeps the sortable headers it has always had.
    const hasTrainingRows = useMemo(() => rowData.some((row) => Boolean(row.isTraining)), [rowData]);

    // Calculated overlay — fully read-only (the wrapper defaults columns to editable: true).
    const defaultColDef: ColDef = useMemo(
        // No filters on this read-only grid → hide the header menu (kebab), like the summary grids.
        // Sorting is dropped only where a training subrow exists (CALL-801): a sort would tear it away
        // from its main row, and the legacy rowSpan the pair is drawn with does not follow a sort either.
        () => ({ editable: false, sortable: !hasTrainingRows, filter: false, resizable: true, suppressMenu: true }),
        [hasTrainingRows]
    );

    const getRowStyle = (params: RowClassParams): RowStyle | undefined =>
        params.node.rowPinned === 'bottom' ? { fontWeight: 'bold' } : undefined;

    return (
        <Box className={SCENARIO_GRID_CLASS} sx={{ opacity: isStale ? 0.6 : 1 }}>
            <TableGridItem
                height="auto"
                domLayout="autoHeight"
                headerHeight={SCENARIO_HEADER_HEIGHT}
                rowHeight={SCENARIO_ROW_HEIGHT}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pinnedBottomRowData={pinnedBottomRowData}
                getRowStyle={getRowStyle}
                getRowClass={(params) => (params.data?.isTraining ? TRAINING_ROW_CLASS : undefined)}
                // The legacy rowSpan (identity cell over the training pair) only draws with row
                // translation off. Costs row animations, which a read-only overlay never had to show.
                suppressRowTransform
                getRowId={(params) =>
                    `${String(params.data.entityId ?? 'total')}${params.data.isTraining ? ':training' : ''}`
                }
                loading={isLoading}
                noRowsOverlayComponent={() => <span style={{ padding: 10 }}>{t('scenarioDetail.noImpact')}</span>}
            />
        </Box>
    );
};

export default ScenarioImpactGrid;
